import { Component, Input, ViewEncapsulation, ElementRef, AfterViewInit, OnDestroy, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MAT_RIPPLE_GLOBAL_OPTIONS } from "@angular/material/core";

@Component({
  selector: "ds-slider",
  templateUrl: "./ds-slider.component.html",
  styleUrls: ["./ds-slider.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsSliderComponent),
      multi: true,
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
  ],
})
export class DsSliderComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() range: boolean = false;
  @Input() min!: number;
  @Input() max!: number;
  @Input() step: number = 1;
  @Input() disabled: boolean = false;
  @Input() displayWith: (value: number) => void;
  value: number = 0;
  valueStart: number = 0;
  valueEnd: number = 0;

  private onChange = (value: number | { start: number; end: number }) => {};
  private onTouched = () => {};
  private mutationObserver?: MutationObserver;

  constructor(private elementRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.addIndicatorClassToThumbs();
  }

  ngOnDestroy(): void {
    this.cleanupMutationObserver();
  }

  onMouseMove(event: MouseEvent): void {
    const knobs = Array.from(
      this.elementRef.nativeElement.querySelectorAll(".mdc-slider__thumb-knob")
    ) as HTMLElement[];

    knobs.forEach((knob) => {
      const rect = knob.getBoundingClientRect();
      const isOverKnob =
        event.clientX + 2 >= rect.left &&
        event.clientX <= rect.right + 2 &&
        event.clientY + 2 >= rect.top &&
        event.clientY <= rect.bottom + 2;

      if (isOverKnob) {
        knob.classList.add("mdc-slider__thumb-knob--hover");
      } else {
        knob.classList.remove("mdc-slider__thumb-knob--hover");
      }
    });
  }

  private addIndicatorClassToThumbs(): void {
    const thumbs = Array.from(
      this.elementRef.nativeElement.querySelectorAll("mat-slider-visual-thumb")
    ) as HTMLElement[];

    thumbs.forEach((thumb) => {
      thumb.classList.add("mdc-slider__thumb--with-indicator");
    });
    this.setupMutationObserver();
  }

  private setupMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const target = mutation.target as HTMLElement;
          if (
            target.tagName === "MAT-SLIDER-VISUAL-THUMB" &&
            !target.classList.contains("mdc-slider__thumb--with-indicator")
          ) {
            target.classList.add("mdc-slider__thumb--with-indicator");
          }
        }
      });
    });
    const thumbs = Array.from(
      this.elementRef.nativeElement.querySelectorAll("mat-slider-visual-thumb")
    ) as HTMLElement[];

    thumbs.forEach((thumb) => {
      this.mutationObserver?.observe(thumb, {
        attributes: true,
        attributeFilter: ["class"],
      });
    });
  }

  private cleanupMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }
  }

  formatLabel = (value: number): string => {
    return `${value}`;
  };

  writeValue(value: number | { start: number; end: number }): void {
    if (this.range && typeof value === "object" && value !== null) {
      this.valueStart = value.start;
      this.valueEnd = value.end;
    } else if (!this.range && typeof value === "number") {
      this.value = value;
    }
  }

  registerOnChange(fn: (value: number | { start: number; end: number }) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSliderChange(value: number): void {
    if (!this.range) {
      this.value = value;
      this.onChange(value);
      this.onTouched();
    }
  }

  onStartSliderChange(event: number): void {
    if (this.range) {
      this.valueStart = event;
      const rangeValue = { start: event, end: this.valueEnd };
      this.onChange(rangeValue);
      this.onTouched();
    }
  }

  onEndSliderChange(event: number): void {
    if (this.range) {
      this.valueEnd = event;
      const rangeValue = { start: this.valueStart, end: event };
      this.onChange(rangeValue);
      this.onTouched();
    }
  }
}
